import { Target } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { usePlanRates } from '@/lib/queries';
import { cn } from '@/lib/utils';
import {
  type GoalStage,
  type PlanBenchmarks,
  type PlanConstants,
  type PlanRates,
  projectPlan,
  rankConstraints,
} from './planMath';

const LOOKBACKS = [7, 14, 30, 60, 90];

const MESSAGES = {
  nb: {
    title: 'Plan',
    sub: 'Sett et mål — planleggeren regner baklengs spend, annonser og funnel fra dine reelle rater.',
    goal: 'Mål',
    stageMembers: 'Nye medlemmer / md',
    stageBookings: 'Trial-bookinger / md',
    lookback: 'Basert på siste',
    days: 'dager',
    retention: 'Mnd som medlem',
    spend: 'Daglig forbruk',
    members: 'Nye medlemmer / dag',
    cpa: 'CPA / medlem',
    revenue: 'Daglig omsetning',
    roi: 'Første-måneds ROI',
    pl: 'Daglig P/L',
    funnelTitle: 'Slik ser planen ut',
    perMonth: '/ md',
    monthlyTitle: 'Månedlig projeksjon',
    ltv: 'Livstidsverdi (antakelse — ingen churn-data)',
    constraintTitle: 'Hva flytter nålen',
    constraintLead:
      'Svakeste steg vs. referanse — den billigste vekst-spaken, uten ekstra forbruk.',
    notEnough: 'For lite data til en plan',
    notEnoughHint: 'Velg et lengre tilbakeblikk, eller vent på flere dager med data.',
    impressions: 'Visninger',
    clicks: 'Klikk',
    bookings: 'Bookinger',
    showedUp: 'Oppmøtt',
    membersStage: 'Medlemmer',
    vsBench: 'mot referanse',
  },
  en: {
    title: 'Plan',
    sub: 'Set a goal — the planner back-solves spend, ads & funnel from your real rates.',
    goal: 'Goal',
    stageMembers: 'New members / mo',
    stageBookings: 'Trial bookings / mo',
    lookback: 'Based on last',
    days: 'days',
    retention: 'Months as member',
    spend: 'Daily spend',
    members: 'New members / day',
    cpa: 'CPA / member',
    revenue: 'Daily revenue',
    roi: 'First-month ROI',
    pl: 'Daily P/L',
    funnelTitle: 'What the plan looks like',
    perMonth: '/ mo',
    monthlyTitle: 'Monthly projection',
    ltv: 'Lifetime value (assumption — no churn data)',
    constraintTitle: 'What moves the needle',
    constraintLead: 'Weakest step vs. reference — the cheapest growth lever, no extra spend.',
    notEnough: 'Not enough data for a plan',
    notEnoughHint: 'Pick a longer lookback, or wait for more days of data.',
    impressions: 'Impressions',
    clicks: 'Clicks',
    bookings: 'Bookings',
    showedUp: 'Showed up',
    membersStage: 'Members',
    vsBench: 'vs reference',
  },
};

export function PlanPage() {
  const m = useViewMessages(MESSAGES);
  const [goalStage, setGoalStage] = useState<GoalStage>('members');
  const [goalMonthly, setGoalMonthly] = useState(30);
  const [lookback, setLookback] = useState(30);
  const [retentionMonths, setRetentionMonths] = useState(6);

  const query = usePlanRates(lookback);

  if (query.isLoading)
    return (
      <PageFrame m={m}>
        <LoadingState />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const data = query.data;
  const rates = data.rates as PlanRates | null;
  const baseConstants = data.constants as unknown as PlanConstants;
  const constants: PlanConstants = { ...baseConstants, defaultRetentionMonths: retentionMonths };
  const benchmarks = data.benchmarks as unknown as PlanBenchmarks;
  // Parity with the legacy gate: the planner needs the cost-per-booking and the
  // lead→close rate to back-solve; without them, show the not-enough-data state.
  const ready = rates !== null && rates.costPerBooking !== null && rates.lcPct !== null;

  return (
    <PageFrame m={m}>
      {/* control bar */}
      <Card className="flex flex-wrap items-end gap-4 p-4">
        <Field label={m.goal}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={goalStage}
              onChange={(e) => setGoalStage(e.target.value as GoalStage)}
              className="h-9 rounded-md border border-border bg-surface-2 px-2 text-body text-fg"
            >
              <option value="members">{m.stageMembers}</option>
              <option value="bookings">{m.stageBookings}</option>
            </select>
            <input
              type="number"
              min={1}
              value={goalMonthly}
              onChange={(e) => setGoalMonthly(Math.max(1, Number(e.target.value) || 0))}
              className="h-9 w-24 rounded-md border border-border bg-surface-2 px-2 text-body text-fg tabular"
            />
          </div>
        </Field>
        <Field label={m.lookback}>
          <select
            value={lookback}
            onChange={(e) => setLookback(Number(e.target.value))}
            className="h-9 rounded-md border border-border bg-surface-2 px-2 text-body text-fg"
          >
            {LOOKBACKS.map((d) => (
              <option key={d} value={d}>
                {d} {m.days}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.retention}>
          <input
            type="number"
            min={1}
            max={60}
            value={retentionMonths}
            onChange={(e) => setRetentionMonths(Math.max(1, Number(e.target.value) || 0))}
            className="h-9 w-20 rounded-md border border-border bg-surface-2 px-2 text-body text-fg tabular"
          />
        </Field>
        <span className="inline-flex items-center gap-1.5 text-label text-fg-subtle sm:ml-auto">
          <CaveatRef id="planDirectional" />
          {data.days} {m.days}
          {data.surArtifact ? <CaveatRef id="backfillSur" /> : null}
        </span>
      </Card>

      {ready && rates ? (
        <PlanBody
          m={m}
          goalStage={goalStage}
          goalMonthly={goalMonthly}
          rates={rates}
          constants={constants}
          benchmarks={benchmarks}
        />
      ) : (
        <EmptyState title={m.notEnough}>{m.notEnoughHint}</EmptyState>
      )}
    </PageFrame>
  );
}

function PlanBody({
  m,
  goalStage,
  goalMonthly,
  rates,
  constants,
  benchmarks,
}: {
  m: (typeof MESSAGES)['nb'];
  goalStage: GoalStage;
  goalMonthly: number;
  rates: PlanRates;
  constants: PlanConstants;
  benchmarks: PlanBenchmarks;
}) {
  const p = projectPlan(goalStage, goalMonthly, rates, constants);
  const constraints = rankConstraints(rates, benchmarks, {
    ctr: m.clicks,
    lpPct: m.bookings,
    surPct: m.showedUp,
    ccPct: m.membersStage,
  });

  const stages: {
    key: string;
    label: string;
    perDay: number | null;
    caveat?: 'showupBlendedRate';
    goal?: boolean;
  }[] = [
    { key: 'impressions', label: m.impressions, perDay: p.perDay.impressions },
    { key: 'clicks', label: m.clicks, perDay: p.perDay.clicks },
    {
      key: 'bookings',
      label: m.bookings,
      perDay: p.perDay.bookings,
      goal: goalStage === 'bookings',
    },
    { key: 'showedUp', label: m.showedUp, perDay: p.perDay.showedUp, caveat: 'showupBlendedRate' },
    {
      key: 'members',
      label: m.membersStage,
      perDay: p.perDay.members,
      goal: goalStage === 'members',
    },
  ];
  const maxValue = stages.reduce((mx, s) => Math.max(mx, s.perDay ?? 0), 0) || 1;

  return (
    <>
      {/* KPI zone */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label={m.spend}
          value={<span className="text-title">{formatMetric(p.perDay.spend, 'nok')}</span>}
          source="meta"
        />
        <StatCard
          label={m.members}
          value={<span className="text-title">{formatMetric(p.perDay.members, 'int')}</span>}
          source="momence"
        />
        <StatCard
          label={m.cpa}
          value={<span className="text-title">{formatMetric(p.cpa, 'nok')}</span>}
          source="derived"
        />
        <StatCard
          label={m.revenue}
          value={<span className="text-title">{formatMetric(p.perDay.revenue, 'nok')}</span>}
          source="stripe"
        />
        <StatCard
          label={m.roi}
          value={
            <span className="inline-flex items-center gap-1">
              <span className="text-title">{formatMetric(p.roi, 'ratio')}</span>
              <CaveatRef id="roiFirstMonth" />
            </span>
          }
          source="derived"
        />
        <StatCard
          label={m.pl}
          value={<span className="text-title">{formatMetric(p.plPerDay, 'nok')}</span>}
          source="derived"
        />
      </div>

      {/* reverse funnel */}
      <section className="flex flex-col gap-2">
        <h2 className="text-section text-fg">{m.funnelTitle}</h2>
        <Card className="flex flex-col gap-2 p-4">
          {stages.map((s) => {
            const widthPct = Math.max(4, ((s.perDay ?? 0) / maxValue) * 100);
            return (
              <div key={s.key} className="flex items-center gap-3">
                <div className="w-20 shrink-0 text-right text-label text-fg-muted sm:w-36">
                  <span className="inline-flex items-center gap-1">
                    {s.goal ? <Target className="size-3.5 text-brand" aria-hidden /> : null}
                    {s.label}
                    {s.caveat ? <CaveatRef id={s.caveat} /> : null}
                  </span>
                </div>
                <div className="relative h-9 flex-1 overflow-hidden rounded-md bg-surface-2">
                  <div
                    className="h-full rounded-md bg-brand/80"
                    style={{ width: `${widthPct}%` }}
                  />
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-label font-medium text-fg tabular">
                    {formatMetric(s.perDay, 'int')}
                  </span>
                </div>
                <div className="hidden w-28 shrink-0 whitespace-nowrap text-left text-label text-fg-subtle tabular sm:block">
                  {formatMetric(s.perDay !== null ? s.perDay * 30 : null, 'int')} {m.perMonth}
                </div>
              </div>
            );
          })}
        </Card>
      </section>

      {/* monthly projection + LTV */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-section text-fg">{m.monthlyTitle}</h2>
          <dl className="mt-3 flex flex-col gap-1.5 text-body">
            <KV k={m.spend} v={formatMetric(p.perMonth.spend, 'nok')} />
            <KV k={m.members} v={formatMetric(p.perMonth.members, 'int')} />
            <KV k={m.revenue} v={formatMetric(p.perMonth.revenue, 'nok')} />
            <KV k={m.pl} v={formatMetric(p.plPerDay !== null ? p.plPerDay * 30 : null, 'nok')} />
          </dl>
        </Card>
        <Card className="p-4">
          <h2 className="text-section text-fg">{m.ltv}</h2>
          <div className="mt-3 text-display text-fg tabular">{formatMetric(p.ltv, 'nok')}</div>
        </Card>
      </div>

      {/* binding constraint + sensitivity */}
      <section className="flex flex-col gap-2">
        <h2 className="text-section text-fg">{m.constraintTitle}</h2>
        <p className="text-label text-fg-muted">{m.constraintLead}</p>
        <Card className="flex flex-col divide-y divide-border p-0">
          {constraints.map((c, i) => (
            <div
              key={c.key}
              className="flex min-w-0 items-center justify-between gap-3 px-4 py-2.5"
            >
              <span className="inline-flex min-w-0 items-center gap-2 text-body text-fg">
                {i === 0 ? <Target className="size-3.5 shrink-0 text-warn" aria-hidden /> : null}
                {c.label}
              </span>
              <span className="shrink-0 text-right text-label text-fg-subtle tabular">
                <span className={cn(i === 0 && c.shortfall > 0 ? 'text-warn-fg' : 'text-fg')}>
                  {formatMetric(c.current !== null ? c.current * 100 : null, 'pct')}
                </span>{' '}
                {m.vsBench} {formatMetric(c.benchmark * 100, 'pct')}
              </span>
            </div>
          ))}
        </Card>
      </section>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  // A div, not a label: some fields wrap multiple controls (goal stage + value).
  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto">
      <span className="text-label text-fg-muted">{label}</span>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-fg-muted">{k}</dt>
      <dd className="text-fg tabular">{v}</dd>
    </div>
  );
}

function PageFrame({ m, children }: { m: (typeof MESSAGES)['nb']; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
      </header>
      {children}
    </div>
  );
}
